import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { rgba } from 'polished';

import MUIGrid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { Typography } from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

import InputValidationErrorTooltip from '@ui-parts/InputValidationErrorTooltip';

import { PALETTE } from '~/assets/styles/base';
import { mixinCircle } from '~/assets/styles/mixin';
import { LIMIT_TOTAL_AMOUNT, LOCALE_STRING } from '~/constants';

const Wrapper = styled(Box)`
  min-height: 200px;
  border: 1px solid;
  border-color: ${({ $error }) => ($error ? rgba(PALETTE.error.main, 0.3) : PALETTE.border.frame)};
  padding: 24px 18px;
  background: ${PALETTE.white};
`;

const Head = styled(MUIGrid)`
  margin-bottom: 16px;
  padding: 0 15px;
  align-items: center;
`;

const Title = styled(MUIGrid)`
  font-weight: bold;
`;

const Note = styled(MUIGrid)`
  text-align: right;
`;

const Body = styled(MUIGrid)`
  margin-bottom: 12px;
  padding: 0 27px 0 17px;
  align-items: center;
`;

const BodyText = styled(MUIGrid)`
  text-align: right;
`;

const Sum = styled(MUIGrid)`
  border-top: 1px solid #e9f0f3;
  padding: 16px 8px 0 17px;
  align-items: center;
`;

const SumText = styled(MUIGrid)`
  text-align: right;
`;

const Edit = styled.button`
  ${mixinCircle}
  margin-left: 40px;
  transition: opacity 0.4s;
  color: #a2b2be;
  &:hover {
    opacity: 0.7;
  }
`;

const initialErrors = {
  message: '',
  invalid: false,
  href: null,
};

function SurveyPriceBox({
  isCompleted,
  openModal,
  numberOfQuestions,
  targetSamplesTotal,
  enqueteCollectCountsTotal,
  canEdit = false,
}) {
  const { t } = useTranslation();
  const validationClickedGoToApply = useSelector((state) => state.validation.clickedGoToApply);
  const surveyPayment = useSelector((state) => state.surveyPayment);
  const [errors, setErrors] = useState(initialErrors);

  const totalAmount = isCompleted ? surveyPayment.priceTotal : surveyPayment.estimatePriceTotal;

  const title = useMemo(
    () =>
      isCompleted
        ? t('quotas.surveyPriceBox.actualAmount')
        : t('quotas.surveyPriceBox.estimatedAmount'),
    [isCompleted]
  );

  const titleOfSampleCounts = useMemo(
    () =>
      isCompleted
        ? t('quotas.surveyPriceBox.enqueteCollectCounts')
        : t('quotas.surveyPriceBox.targetSamplesTotal'),
    [isCompleted]
  );

  const sampleCounts = isCompleted ? enqueteCollectCountsTotal : targetSamplesTotal;

  useEffect(() => {
    setErrors(validationClickedGoToApply.errors.surveyPrice);
  }, [validationClickedGoToApply.errors.surveyPrice]);

  return (
    <Wrapper $error={errors.invalid} data-testid="uiParts-SurveyPriceBox">
      <Head container>
        <Title item xs={3} data-testid="uiParts-SurveyPriceBox-Title">
          <Typography variant="title3">{title}</Typography>
        </Title>
        {canEdit && (
          <Note item xs={9} data-testid="uiParts-SurveyPriceBox-Note">
            <Typography variant="note1">
              {t('quotas.surveyPriceBox.cationTotal', {
                minAmount: LIMIT_TOTAL_AMOUNT.MIN.toLocaleString(LOCALE_STRING.ja),
              })}
            </Typography>
          </Note>
        )}
      </Head>

      <Body container>
        <MUIGrid item xs={4}>
          <Typography variant="title3">{t('quotas.surveyPriceBox.numberOfQuestions')}</Typography>
        </MUIGrid>
        <BodyText item xs={6}>
          <Typography variant="body1" data-testid="uiParts-SurveyPriceBox-numberOfQuestions">
            {numberOfQuestions.toLocaleString(LOCALE_STRING.ja)}
          </Typography>
        </BodyText>
        <MUIGrid item xs={2} hidden={!canEdit}>
          <Edit onClick={() => openModal()} data-testid="uiParts-SurveyPriceBox-editButton">
            <EditRoundedIcon />
          </Edit>
        </MUIGrid>
        <MUIGrid item xs={4}>
          <Typography variant="title3" data-testid="uiParts-SurveyPriceBox-titleOfSampleCounts">
            {titleOfSampleCounts}
          </Typography>
        </MUIGrid>
        <BodyText item xs={6} data-testid="uiParts-SurveyPriceBox-sampleCounts">
          <Typography variant="body1">{sampleCounts.toLocaleString(LOCALE_STRING.ja)}</Typography>
        </BodyText>
      </Body>

      <Sum container>
        <MUIGrid item xs={3}>
          <Typography variant="title3">{t('quotas.surveyPriceBox.total')}</Typography>
        </MUIGrid>

        <SumText item xs={9}>
          <InputValidationErrorTooltip
            error={errors.invalid}
            message={errors.message}
            dataTestId="SurveyPriceBox"
            inquiryLink={errors.href}
          >
            <Typography
              variant="body1"
              component="span"
              color={errors.invalid && 'error'}
              data-testid="uiParts-SurveyPriceBox-total"
            >
              {targetSamplesTotal === 0
                ? 0
                : parseInt(totalAmount, 10).toLocaleString(LOCALE_STRING.ja)}
              {t('quotas.surveyPriceBox.yenWithTax')}
            </Typography>
          </InputValidationErrorTooltip>
        </SumText>
      </Sum>
    </Wrapper>
  );
}

SurveyPriceBox.propTypes = {
  isCompleted: PropTypes.bool.isRequired,
  openModal: PropTypes.func,
  numberOfQuestions: PropTypes.number.isRequired,
  targetSamplesTotal: PropTypes.number.isRequired,
  enqueteCollectCountsTotal: PropTypes.number.isRequired,
  canEdit: PropTypes.bool,
};

export default SurveyPriceBox;
